import React from 'react';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { Sparkles, Crown, Rocket } from 'lucide-react';
import { Card, CardContent } from '../ui/card';
import { PricingCard } from './PricingCard';
import { fadeInUp, smoothTransition, cardAnimation } from './animations';

const plans = [
{
  id: 'plan-start',
  name: 'Zappien Start',
  price: "$1,699",
  currency: 'MXN',
  icon: Sparkles,
  description: 'Ideal para empezar rápido y validar el sistema',
  features: [
  'Menú digital interactivo',
  'Diseño personalizado básico',
  'Código QR listo para mesas',
  '1 actualización mensual',
  '12 meses de acceso completo'],

  highlighted: false
},
{
  id: 'plan-growth',
  name: 'Zappien Growth',
  price: "$2,199",
  currency: 'MXN',
  icon: Crown,
  badge: 'Recomendado',
  description: 'La opción recomendada para aumentar ventas desde el inicio',
  features: [
  'Todo lo de Zappien Start',
  'Optimización estratégica de ventas',
  'Estructura avanzada del menú',
  'Destacado de productos clave',
  'Integración con redes sociales',
  '2 actualizaciones mensuales',
  'Soporte prioritario'],

  highlighted: true
},
{
  id: 'plan-scale',
  name: 'Zappien Scale',
  price: "$4,500",
  currency: 'MXN',
  icon: Rocket,
  description: 'Pensado para negocios que quieren escalar y maximizar resultados',
  features: [
  'Todo lo de Zappien Growth',
  'Fotos con IA para menú visual premium',
  'Automatización básica',
  'Optimización continua',
  'Consultoría estratégica',
  'Prioridad máxima en soporte'],

  highlighted: false
}];


export const Pricing = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  return (
    <section ref={ref} className="py-20 sm:py-28 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      <div className="absolute inset-0 bg-gradient-to-b from-background via-muted/20 to-background" />
      
      <div className="container px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div
          {...fadeInUp}
          animate={inView ? fadeInUp.animate : fadeInUp.initial}
          transition={smoothTransition}
          className="text-center max-w-3xl mx-auto mb-16">

          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-6">
            Elige cómo quieres empezar a <span className="text-gradient">vender más</span> con tu menú
          </h2>
          <p className="text-lg text-muted-foreground">
            Todos los planes incluyen 12 meses de acceso completo. Renovación anual: $600 MXN.
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-3 gap-6 lg:gap-8 max-w-7xl mx-auto">
          {plans.map((plan, index) =>
          <PricingCard
            key={plan.id}
            plan={plan}
            index={index}
            highlighted={plan.highlighted} />

          )}
        </div>

        <motion.div
          {...cardAnimation(0.4)}
          animate={inView ? cardAnimation(0.4).animate : cardAnimation(0.4).initial}
          className="mt-16 max-w-4xl mx-auto">

          <Card className="glass-card border-[hsl(var(--cta-orange))]/30">
            <CardContent className="p-8">
              <h3 className="text-2xl font-bold mb-4 text-center">
                ¿Por qué es una inversión inteligente?
              </h3>
              <p className="text-muted-foreground leading-relaxed mb-4">
                No estás pagando por un menú, estás invirtiendo en una herramienta que influye directamente en cuánto consume cada cliente que entra a tu negocio.
              </p>
              <p className="text-muted-foreground leading-relaxed mb-4">
                Si con este sistema logras que cada cliente gaste solo <span className="text-primary font-semibold">$20–$30 pesos más</span>, en pocos días recuperas la inversión y a partir de ahí todo es ganancia.
              </p>
              <p className="text-foreground font-medium text-center">
                La pregunta no es si puedes permitirte invertir en esto, es <span className="text-[hsl(var(--cta-orange))]">cuánto tiempo más quieres seguir perdiendo dinero</span> con un menú que no está diseñado para vender.
              </p>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </section>);

};